import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Events } from '@df/core/service/event';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { IPageComponentSubmenuData } from '../../catalog/ui/page/component/submenu/submenu.interface';
import { NCustomerGroups } from '../../customer/groups/customer-groups';
import { CustomerGroupsService } from '../../customer/groups/customer-groups.service';
import { GtmTrackClickDirective } from '../../module/gtm/gtm-track-click.directive';
import { PageHeaderComponent } from '../page/header/page-header.component';
import { NPageHeader } from '../page/header/page-header.interface';
import { CsOverrideIdDirective } from './cs-override-id.directive';

/**
 * Meganav component.
 *
 * @usage
 * ```html
 * <meganav items="content.meganav"></meganav>
 * ```
 */
@Component({
    selector: 'meganav',
    templateUrl: './meganav.html',
    styleUrl: './meganav.scss',
    standalone: true,
    imports: [
        CommonModule,
        CsOverrideIdDirective,
        GtmTrackClickDirective,
        HrefComponent,
        ICmsClientModule,
        OffClickDirective,
        ToggleModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeganavComponent extends UiComponent {
    @Input({ required: true })
    items: NPageHeader.IMenuItem[] = [];

    /**
     * Group id for toggle service
     */
    readonly toggleGroupId = NPageHeader.MEGANAV_GROUP_ID;

    protected events = inject(Events);
    protected toggleService = inject(ToggleService);
    protected meta = inject(Meta);
    protected customerGroupsService = inject(CustomerGroupsService);
    protected app = inject(App);
    protected pageHeaderComponent = inject(PageHeaderComponent);

    override ngOnInit(): void {
        this.subscriptions.add(this.meta.subscribe(() => this.toggleService.closeGroup(this.toggleGroupId)));
        this.subscriptions.add(this.app.customer.model.obs.status.subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.pageHeaderComponent.hasContent$.subscribe(() => {
                this.detectChanges();
            })
        );

        super.ngOnInit();
    }

    get isCms(): boolean {
        return this.client.isCms && !this.client.isCmsPreview;
    }

    handleMouseEnter(index: number, item: NPageHeader.IMenuItem): void {
        if (!this.isCms && this.hasSubmenu(item)) {
            this.toggleService.open([this.toggleGroupId, index]);

            this.events.broadcast('meganav:open', { index, item });
        }
    }

    handleMouseLeave(index: number): void {
        if (!this.isCms) {
            this.toggleService.close([this.toggleGroupId, index], { delay: 300 });
        }
    }

    handleClick(index: number): void {
        if (this.isCms) {
            this.toggleService.toggle([this.toggleGroupId, index]);
        }
    }

    close() {
        this.toggleService.closeGroup(NPageHeader.MEGANAV_GROUP_ID, { exceptionItems: ['search'] });
    }

    ifCustomerGroup(value: NCustomerGroups.TId | NCustomerGroups.TId[]): boolean {
        return this.customerGroupsService.isAllowed(value);
    }

    /**
     * in icms all can have submenu, outside only when  its set and its valid
     */
    protected hasSubmenu(item: NPageHeader.IMenuItem): boolean {
        return !!item?.components?.find(
            (component: IPageComponentSubmenuData) =>
                !!component?.columns?.find(column => {
                    if (!column.hide) {
                        return !!column.blocks?.find(block => !block.hide);
                    }

                    return false;
                })
        );
    }
}
